import {
  ID,
  newTaskSuffix,
  rootId,
  SortingMode,
  Task,
  TaskListItemTypes,
  TaskListTreeItem,
  TaskPriorityOptions,
  Timestamp,
  WeekDays,
} from '@griply/types';

import { getSortedTasks } from '../get-sorted-tasks';
import { filterEmptyChildrenCascading } from './filter-empty-children-cascading';

export type BuildPriorityTreeOptions = {
  tasks: Task[];
  customOrder?: Record<ID, ID[]>;
  sortingMode?: SortingMode | null;
  today: Timestamp;
  weekStartsOn: WeekDays;
  showEmptyGroups?: boolean;
  hideNewTaskRow?: boolean;
  priorityLabels: Record<TaskPriorityOptions, string>;
  rootLabel: string;
  newTaskLabel: string;
};

export const buildPriorityTree = ({
  tasks,
  customOrder,
  sortingMode,
  today,
  weekStartsOn,
  showEmptyGroups,
  hideNewTaskRow,
  priorityLabels,
  rootLabel,
  newTaskLabel,
}: BuildPriorityTreeOptions): Record<ID, TaskListTreeItem> => {
  const priorityTreeItems: Record<ID, TaskListTreeItem> = {
    [TaskPriorityOptions.High]: {
      id: TaskPriorityOptions.High,
      type: TaskListItemTypes.Priority,
      name: priorityLabels[TaskPriorityOptions.High],
      children: [],
      isFolder: true,
    },
    [TaskPriorityOptions.Medium]: {
      id: TaskPriorityOptions.Medium,
      type: TaskListItemTypes.Priority,
      name: priorityLabels[TaskPriorityOptions.Medium],
      children: [],
      isFolder: true,
    },
    [TaskPriorityOptions.Low]: {
      id: TaskPriorityOptions.Low,
      type: TaskListItemTypes.Priority,
      name: priorityLabels[TaskPriorityOptions.Low],
      children: [],
      isFolder: true,
    },
    [TaskPriorityOptions.None]: {
      id: TaskPriorityOptions.None,
      type: TaskListItemTypes.Priority,
      name: priorityLabels[TaskPriorityOptions.None],
      children: [],
      isFolder: true,
    },
  };
  const priorityIds = Object.keys(priorityTreeItems);

  const customTaskOrder =
    sortingMode === SortingMode.Custom
      ? priorityIds.reduce<ID[]>((acc, priority) => {
          const order = customOrder?.[priority] ?? [];
          acc.push(...order);
          return acc;
        }, [])
      : [];

  const allTreeItems = getSortedTasks(tasks, {
    today,
    weekStartsOn,
    order: customTaskOrder,
    mode: sortingMode ? [SortingMode.DueDate, sortingMode] : undefined,
  }).reduce<Record<ID, TaskListTreeItem>>((acc, task) => {
    const priorityTreeItem =
      acc[task.priority!] ?? acc[TaskPriorityOptions.None];
    priorityTreeItem.children?.push(task.id);
    acc[task.id] = {
      id: task.id,
      name: task.name,
      type: TaskListItemTypes.Task,
    };
    return acc;
  }, priorityTreeItems);

  if (!hideNewTaskRow) {
    Object.keys(priorityTreeItems).forEach((priorityId) => {
      const priorityTreeItem = allTreeItems[priorityId];
      if (!priorityTreeItem) {
        return;
      }
      if (!priorityTreeItem.children?.length && !showEmptyGroups) {
        return;
      }

      const newTaskItemId = `${priorityId}${newTaskSuffix}`;
      priorityTreeItem.children?.push(newTaskItemId);
      allTreeItems[newTaskItemId] = {
        id: newTaskItemId,
        name: newTaskLabel,
        type: TaskListItemTypes.NewTask,
      };
    });
  }

  const filteredTreeItems = filterEmptyChildrenCascading(allTreeItems);
  const topLevelItemIds = Object.keys(filteredTreeItems).filter(
    (id) => filteredTreeItems[id].type === TaskListItemTypes.Priority,
  );

  return {
    [rootId]: {
      id: rootId,
      name: rootLabel,
      type: TaskListItemTypes.Root,
      children: topLevelItemIds,
    },
    ...allTreeItems,
  };
};
